/**
 * ConfigModal - Setup modal for Mixpanel credentials
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { PluginConfigModalProps } from '../_infrastructure/pluginTypes';
import { validateCredentials } from './lib/mixpanel';

type ConfigStep = 'credentials' | 'validating' | 'success' | 'error';

export function ConfigModal({
  isOpen,
  onClose,
  config,
  onSave,
}: PluginConfigModalProps) {
  const hasStoredServiceAccount = Boolean((config as { hasServiceAccount?: boolean }).hasServiceAccount);
  const [apiSecret, setApiSecret] = useState(config.apiSecret || '');
  const [projectId, setProjectId] = useState(config.projectId || '');
  const [serviceAccountUser, setServiceAccountUser] = useState('');
  const [serviceAccountSecret, setServiceAccountSecret] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(hasStoredServiceAccount);
  const [serviceAccountMode, setServiceAccountMode] = useState<'none' | 'update' | 'remove'>('none');
  const [step, setStep] = useState<ConfigStep>('credentials');
  const [errorMessage, setErrorMessage] = useState('');

  const handleToggleAdvanced = useCallback(() => {
    setShowAdvanced((prev) => {
      const next = !prev;
      if (next && !hasStoredServiceAccount && serviceAccountMode === 'none') {
        setServiceAccountMode('update');
      }
      if (!next) {
        setServiceAccountMode('none');
        setServiceAccountUser('');
        setServiceAccountSecret('');
      }
      return next;
    });
  }, [hasStoredServiceAccount, serviceAccountMode]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!apiSecret.trim() || !projectId.trim()) {
        setErrorMessage('Both fields are required');
        setStep('error');
        return;
      }

      setStep('validating');
      setErrorMessage('');

      try {
        const result = await validateCredentials({
          apiSecret: apiSecret.trim(),
          projectId: projectId.trim(),
        });

        if (result.valid) {
          setStep('success');
          setTimeout(async () => {
            const configToSave: Record<string, string | boolean> = {
              apiSecret: apiSecret.trim(),
              projectId: projectId.trim(),
            };

            if (serviceAccountMode === 'update') {
              if (!serviceAccountUser.trim() || !serviceAccountSecret.trim()) {
                setErrorMessage('Enter both Service Account username and secret.');
                setStep('error');
                return;
              }
              configToSave.serviceAccountUser = serviceAccountUser.trim();
              configToSave.serviceAccountSecret = serviceAccountSecret.trim();
            } else if (serviceAccountMode === 'remove' && hasStoredServiceAccount) {
              configToSave.removeServiceAccount = true;
            }

            // Await the save so a persistence failure surfaces in the modal
            // instead of silently dismissing it (credentials would appear saved
            // but be lost). Only close on success.
            try {
              await onSave(configToSave);
              onClose();
            } catch (saveErr) {
              setErrorMessage(
                saveErr instanceof Error
                  ? `Failed to save credentials: ${saveErr.message}`
                  : 'Failed to save credentials. Please try again.',
              );
              setStep('error');
            }
          }, 1000);
        } else {
          setErrorMessage(result.error || 'Invalid credentials. Check your API secret.');
          setStep('error');
        }
      } catch (err) {
        setErrorMessage('Failed to connect. Check your credentials and try again.');
        setStep('error');
      }
    },
    [apiSecret, projectId, serviceAccountMode, serviceAccountUser, serviceAccountSecret, hasStoredServiceAccount, onSave, onClose]
  );

  const handleClose = useCallback(() => {
    setStep('credentials');
    setErrorMessage('');
    setServiceAccountMode('none');
    setServiceAccountUser('');
    setServiceAccountSecret('');
    onClose();
  }, [hasStoredServiceAccount, onClose]);

  // Ref for the modal container (for focus management)
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Focus the modal when it opens
  useEffect(() => {
    if (isOpen) {
      setApiSecret(config.apiSecret || '');
      setProjectId(config.projectId || '');
      setServiceAccountUser('');
      setServiceAccountSecret('');
      setServiceAccountMode('none');
      setShowAdvanced(hasStoredServiceAccount);
      if (modalRef.current) {
        modalRef.current.focus();
      }
    }
  }, [isOpen, config.apiSecret, config.projectId, hasStoredServiceAccount]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="config-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl" aria-hidden="true">📊</span>
            <h2
              id="config-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              Connect Mixpanel
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">
                Validating credentials...
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✅</div>
              <p className="text-green-600 font-medium">Connected successfully!</p>
            </div>
          )}

          {(step === 'credentials' || step === 'error') && (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm">
                <p className="text-blue-800 font-medium mb-2">
                  API Secret (Required)
                </p>
                <p className="text-blue-700 text-xs mb-2">
                  Enables: Dashboard with DAU, MAU, retention, and 30-day trends
                </p>
                <ol className="text-blue-600 space-y-1 list-decimal list-inside text-xs">
                  <li>Go to Mixpanel → Project Settings → Access Keys</li>
                  <li>Copy your <strong>Project API Secret</strong></li>
                  <li>Copy your <strong>Project ID</strong> from the URL</li>
                </ol>
              </div>

              {/* Error message */}
              {step === 'error' && errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              {/* API Secret */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Secret
                </label>
                <input
                  type="password"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Your Mixpanel API secret"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Found in Project Settings → Access Keys
                </p>
              </div>

              {/* Project ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project ID
                </label>
                <input
                  type="text"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  placeholder="Your Mixpanel project ID"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  From URL: mixpanel.com/project/<strong>1234567</strong>
                </p>
              </div>

              {/* Service Account for Advanced Queries */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={handleToggleAdvanced}
                  className="flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900"
                >
                  <span className={`transform transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>▶</span>
                  Service Account (for advanced queries)
                </button>

                {showAdvanced && (
                  <div className="mt-3 space-y-3 pl-4 border-l-2 border-purple-200">
                    <div className="bg-purple-50 rounded-lg p-3 text-sm">
                      <p className="text-purple-800 font-medium mb-1">
                        Unlocks Advanced Queries:
                      </p>
                      <ul className="text-purple-700 text-xs space-y-0.5 list-disc list-inside">
                        <li>"Top 10 days for sessions" (full year data)</li>
                        <li>"Sessions by country" / "DAU by city"</li>
                        <li>"Any anomalies?" (spike & drop detection)</li>
                        <li>"Top 10 users" (leaderboard)</li>
                        <li>"Give me insights" (AI-powered)</li>
                      </ul>
                      <p className="text-purple-600 mt-2 text-xs">
                        Create at: <strong>Org Settings → Service Accounts</strong> (requires Analyst role)
                      </p>
                    </div>

                    {hasStoredServiceAccount && serviceAccountMode === 'none' && (
                      <div className="bg-white border border-purple-100 rounded-lg p-3 text-xs text-purple-700">
                        <p className="font-medium">
                          A Service Account is already connected.
                        </p>
                        <p>
                          Update to rotate credentials or remove access entirely.
                        </p>
                      </div>
                    )}

                    {(serviceAccountMode === 'update' || !hasStoredServiceAccount) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Account Username
                          </label>
                          <input
                            type="text"
                            value={serviceAccountUser}
                            onChange={(e) => setServiceAccountUser(e.target.value)}
                            placeholder="e.g., xavior.123456.mp-service-account"
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Account Secret
                          </label>
                          <input
                            type="password"
                            value={serviceAccountSecret}
                            onChange={(e) => setServiceAccountSecret(e.target.value)}
                            placeholder="Service account secret"
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                        </div>
                      </>
                    )}

                    {serviceAccountMode === 'remove' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                        Removing the Service Account disables advanced analytics queries until new credentials are added.
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 text-xs">
                      {serviceAccountMode !== 'update' && (
                        <button
                          type="button"
                          onClick={() => setServiceAccountMode('update')}
                          className="px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700"
                        >
                          {hasStoredServiceAccount ? 'Update credentials' : 'Add credentials'}
                        </button>
                      )}
                      {hasStoredServiceAccount && serviceAccountMode !== 'remove' && (
                        <button
                          type="button"
                          onClick={() => setServiceAccountMode('remove')}
                          className="px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          Remove credentials
                        </button>
                      )}
                      {serviceAccountMode !== 'none' && (
                        <button
                          type="button"
                          onClick={() => setServiceAccountMode('none')}
                          className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                >
                  Connect
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
